import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './LegalModal.css';

const TABS = [
  { id: 'terms',   label: 'Terms of Service' },
  { id: 'privacy', label: 'Privacy Policy'   },
  { id: 'refund',  label: 'Refund Policy'    },
];

function TermsContent() {
  return (
    <div className="legal__content">
      <h2>Terms of Service</h2>
      <p><em>Last updated: June 2026</em></p>

      <h3>1. Acceptance</h3>
      <p>By using Motoroam you agree to these terms. If you don't agree, don't use the service.</p>

      <h3>2. The Service</h3>
      <p>Motoroam is a motorcycle route planning and community platform. We provide tools to plan rides, share routes, and organize group tours. We are not a transport provider and are not responsible for any decisions made while riding.</p>

      <h3>3. Accounts</h3>
      <p>You must sign in with a Google account. You are responsible for all activity under your account. You must be 18 or older to use Motoroam.</p>

      <h3>4. Pro Subscription</h3>
      <p>Pro plans are billed monthly or annually via Stripe. You can cancel at any time via the billing portal; access continues until the end of the current period. We reserve the right to change pricing with 30 days' notice.</p>

      <h3>5. Tours & Payments</h3>
      <p>Publishing a tour is free. For paid tickets, a 5% booking fee is added to the ticket price at checkout and paid by the attendee; the organizer's set ticket price is transferred directly to their connected Stripe account. Motoroam is not a party to the contract between organizers and attendees.</p>

      <h3>6. Content</h3>
      <p>You retain ownership of content you post (routes, tour descriptions, images). By posting, you grant Motoroam a worldwide, royalty-free licence to display and distribute that content on the platform. You must not post illegal, offensive, or misleading content.</p>

      <h3>7. Liability</h3>
      <p>Motoroam is provided "as is". We are not liable for any injuries, accidents, losses, or damages arising from use of the platform or participation in tours. Motorcycling is inherently risky — ride safely and within your abilities.</p>

      <h3>8. Termination</h3>
      <p>We may suspend or terminate accounts that violate these terms without notice.</p>

      <h3>9. Governing Law</h3>
      <p>These terms are governed by the laws of the jurisdiction where Motoroam is incorporated.</p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="legal__content">
      <h2>Privacy Policy</h2>
      <p><em>Last updated: June 2026</em></p>

      <h3>What we collect</h3>
      <ul>
        <li><strong>Account data:</strong> name, email, and profile photo from Google Sign-In.</li>
        <li><strong>Usage data:</strong> routes you plan and save, tours you create or register for.</li>
        <li><strong>Payment data:</strong> handled entirely by Stripe — we never see or store card numbers.</li>
        <li><strong>Location:</strong> only used in-browser for the "Near Me" feature — never stored on our servers.</li>
      </ul>

      <h3>How we use it</h3>
      <ul>
        <li>To provide and improve the service.</li>
        <li>To process payments and manage subscriptions via Stripe.</li>
        <li>To show your name and photo on routes and tours you publish.</li>
      </ul>

      <h3>Third parties</h3>
      <p>We use <strong>Firebase</strong> (Google) for authentication, database, and file storage. We use <strong>Stripe</strong> for payments. Both have their own privacy policies. We use <strong>OpenRouteService</strong> and <strong>Overpass API</strong> for mapping — route data may be sent to these services to calculate directions.</p>

      <h3>Data retention</h3>
      <p>Your account data is retained until you request deletion. Published routes and tours may remain visible after account deletion unless you delete them first.</p>

      <h3>Your rights</h3>
      <p>You may request access to, correction of, or deletion of your personal data by contacting us. For payment data, contact Stripe directly.</p>

      <h3>Contact</h3>
      <p>For privacy enquiries: <strong>info@motoroam.app</strong></p>
    </div>
  );
}

function RefundContent() {
  return (
    <div className="legal__content">
      <h2>Refund Policy</h2>
      <p><em>Last updated: June 2026</em></p>

      <h3>Booking fee</h3>
      <p>Publishing a tour is free. A 5% booking fee is added to paid tickets at checkout. The booking fee is <strong>non-refundable</strong> except where the ticket itself is refunded due to organizer cancellation.</p>

      <h3>Paid tour tickets</h3>
      <p>Refunds for tour tickets are at the discretion of the tour organizer. Motoroam does not issue refunds on behalf of organizers. If an organizer cancels their tour, registered attendees should contact the organizer directly to arrange a refund.</p>
      <p>If you are unable to resolve a dispute with an organizer, contact us and we will mediate where possible.</p>

      <h3>Pro subscriptions</h3>
      <p>Subscription fees are non-refundable for the current billing period. You may cancel at any time and your access will continue until the period ends. No partial refunds are issued for unused time.</p>

      <h3>Contact</h3>
      <p>For payment issues: <strong>info@motoroam.app</strong></p>
    </div>
  );
}

export function LegalModal({ initialTab = 'terms', onClose }) {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="legal-overlay" onClick={e => e.target.classList.contains('legal-overlay') && onClose()}>
      <div className="legal-panel">
        <div className="legal__header">
          <div className="legal__tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`legal__tab${tab === t.id ? ' legal__tab--active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button className="legal__close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>
        <div className="legal__body">
          {tab === 'terms'   && <TermsContent />}
          {tab === 'privacy' && <PrivacyContent />}
          {tab === 'refund'  && <RefundContent />}
        </div>
      </div>
    </div>,
    document.body
  );
}
